/**
 * Карточка подписки в мини-аппе: главное из сайта под «VPN не подключается». Сверху
 * то, что может мешать подключению, ниже ссылка и UUID для копирования, новая ссылка и
 * сброс устройств, платежи и история. Журнал подключений и поштучная работа с
 * устройствами остались на сайте: на телефоне это длинные таблицы.
 */
import { useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { CheckCircle2, ChevronRight, Link2, Loader2, RotateCcw } from 'lucide-react';
import { api, apiErrorMessage } from '@/lib/api';
import type { OwnerSubscriptionCard } from '@/api/types';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { BottomSheet } from '@/components/ui/dialog';
import { CopyValue, shortId } from '@/components/CopyValue';
import { Skeleton } from '@/components/ui/skeleton';
import { haptic } from '@/lib/telegram';
import { useTgBack } from '@/lib/use-back';
import {
  TONE_CLS,
  auditLabel,
  fmtDate,
  fmtDateTime,
  ledgerLabel,
  money,
  paymentPurpose,
  paymentStatus,
  providerName,
  subscriptionHint,
  type Tone,
} from '@/lib/labels';

export const Route = createFileRoute('/subscriptions/$id')({ component: SubscriptionCardPage });

const rtf = new Intl.RelativeTimeFormat('ru', { numeric: 'auto' });

/** «3 часа назад»: саппорту важно, давно ли клиент подключался, а не точная дата. */
function ago(iso: string | null): string {
  if (!iso) return 'никогда';
  const diff = (new Date(iso).getTime() - Date.now()) / 1000;
  const abs = Math.abs(diff);
  if (abs < 3600) return rtf.format(Math.round(diff / 60), 'minute');
  if (abs < 86_400) return rtf.format(Math.round(diff / 3600), 'hour');
  return rtf.format(Math.round(diff / 86_400), 'day');
}

function fmtBytes(b: number | null | undefined): string {
  if (b == null) return '—';
  const gb = b / 1024 ** 3;
  if (gb >= 1) return `${gb.toLocaleString('ru-RU', { maximumFractionDigits: 1 })} ГБ`;
  return `${Math.round(b / 1024 ** 2)} МБ`;
}

function SubscriptionCardPage() {
  const { id } = Route.useParams();
  useTgBack();
  const card = useQuery({
    queryKey: ['owner-sub-card', id],
    queryFn: () => api.subscriptionCard(id),
  });
  if (card.isPending) return <CardSkeleton />;
  if (card.isError) {
    return (
      <div className="p-4 pt-10 text-center space-y-3">
        <p className="text-sm text-muted-foreground">Карточка не загрузилась</p>
        <Button variant="secondary" size="sm" onClick={() => card.refetch()}>
          Повторить
        </Button>
      </div>
    );
  }
  return <SubscriptionView c={card.data} />;
}

function CardSkeleton() {
  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 space-y-2">
        <Skeleton className="h-7 w-2/3" />
        <Skeleton className="h-4 w-1/2" />
      </div>
      <Skeleton className="h-20 w-full rounded-2xl" />
      <Skeleton className="h-28 w-full rounded-2xl" />
      <Skeleton className="h-40 w-full rounded-2xl" />
    </div>
  );
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span className={`shrink-0 whitespace-nowrap text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${TONE_CLS[tone]}`}>
      {children}
    </span>
  );
}

function Title({ children }: { children: React.ReactNode }) {
  return <h3 className="text-xs font-bold uppercase tracking-wider text-muted-foreground mb-2">{children}</h3>;
}

function SubscriptionView({ c }: { c: OwnerSubscriptionCard }) {
  const ag = c.supplier === 'adaptgroup';
  const [rotateOpen, setRotateOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const clientName = c.client
    ? c.client.tg_username
      ? `@${c.client.tg_username}`
      : (c.client.email ?? (c.client.tg_user_id != null ? `ID ${c.client.tg_user_id}` : 'клиент'))
    : 'клиент удалён';
  const limit = c.live.traffic_limit_bytes ?? c.traffic_limit_bytes;

  return (
    <div className="p-4 space-y-4 pb-32">
      <div className="px-2 pt-2 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="min-w-0 text-xl font-bold tracking-tight [overflow-wrap:anywhere]">{c.plan_name}</h2>
          {c.is_frozen ? (
            <Pill tone="blue">заморожена</Pill>
          ) : c.is_active ? (
            <Pill tone="green">активна</Pill>
          ) : (
            <Pill tone="dim">истекла</Pill>
          )}
          {c.is_trial && <Pill tone="amber">пробная</Pill>}
          {!ag && <Pill tone="dim">FranCenter</Pill>}
        </div>
        <p className="mt-1 text-xs text-muted-foreground [overflow-wrap:anywhere]">
          {c.client ? (
            <Link to="/clients/$id" params={{ id: c.client.id }} className="text-primary">
              {clientName}
            </Link>
          ) : (
            clientName
          )}
          {c.bot && ` · @${c.bot.username}`}
          {c.partner && (
            <>
              {' · '}
              <Link to="/partners/$id" params={{ id: c.partner.id }} className="text-primary">
                {c.partner.display_name ?? 'партнёр'}
              </Link>
            </>
          )}
          {` · куплена ${fmtDate(c.created_at)}`}
        </p>
      </div>

      <Hints c={c} />

      <div className="grid grid-cols-2 gap-2">
        <Tile
          label="Срок"
          value={c.is_lifetime ? 'навсегда' : fmtDate(c.end_date)}
          sub={c.live.ok && c.live.end_date && !c.is_lifetime ? `у AdaptGroup ${fmtDate(c.live.end_date)}` : undefined}
        />
        <Tile label="Устройств" value={String(c.devices)} sub="лимит тарифа" />
        <Tile label="Трафик" value={fmtBytes(c.live.used_traffic_bytes)} sub={limit ? `из ${fmtBytes(limit)}` : 'без лимита'} />
        <Tile
          label="Подключение"
          value={ago(c.last_connected_at)}
          sub={c.first_connected_at ? `впервые ${fmtDate(c.first_connected_at)}` : 'ни разу'}
        />
      </div>

      <Card>
        <CardContent className="p-5 space-y-3">
          <Title>Ссылка и идентификаторы</Title>
          <dl className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-x-3 gap-y-1 text-sm">
            <dt className="text-muted-foreground text-xs">Ссылка</dt>
            <dd className="min-w-0">
              <CopyValue value={c.subscription_url} title="ссылка подписки" />
            </dd>
            <dt className="text-muted-foreground text-xs">{ag ? 'UUID' : 'FranCenter'}</dt>
            <dd className="min-w-0">
              <CopyValue value={c.upstream_id} title={ag ? 'UUID AdaptGroup' : 'пользователь FranCenter'} />
            </dd>
            <dt className="text-muted-foreground text-xs">id</dt>
            <dd className="min-w-0">
              <CopyValue value={c.id} title="id подписки" />
            </dd>
            {c.link_rotated_at && (
              <>
                <dt className="text-muted-foreground text-xs">Ссылку меняли</dt>
                <dd className="text-xs">{fmtDateTime(c.link_rotated_at)}</dd>
              </>
            )}
          </dl>
          <div className="grid gap-2 pt-1">
            <Button variant="secondary" onClick={() => setRotateOpen(true)}>
              <Link2 /> Выдать новую ссылку
            </Button>
            {ag && (
              <Button variant="secondary" onClick={() => setResetOpen(true)}>
                <RotateCcw /> Сбросить все устройства
              </Button>
            )}
            {c.client && (
              <Link
                to="/clients/$id"
                params={{ id: c.client.id }}
                className="flex min-h-11 items-center justify-center gap-1 text-sm text-primary"
              >
                Продлить, заморозить, перенести <ChevronRight className="w-4 h-4" />
              </Link>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <Title>Платежи по подписке</Title>
          {!c.payments.length ? (
            <p className="text-sm text-muted-foreground">
              Платежей нет: подписку выдали без счёта (баланс, подарок, ваучер, пробная)
            </p>
          ) : (
            <div className="divide-y divide-white/5">
              {c.payments.map((p) => {
                const st = paymentStatus(p.status);
                return (
                  <Link key={p.id} to="/payments/$id" params={{ id: p.id }} className="flex items-center gap-2 py-2 active:bg-white/5">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm font-semibold truncate">
                        {money(p.amount_minor, p.currency)} · {providerName(p.provider)}
                      </div>
                      <div className="text-[11px] text-muted-foreground truncate">
                        {fmtDate(p.created_at)}
                        {p.kind && ` · ${paymentPurpose(p.kind)}`}
                      </div>
                      <CopyValue value={p.provider_invoice_id} label={shortId(p.provider_invoice_id)} title="номер у кассы" className="text-muted-foreground" />
                    </div>
                    <Pill tone={st.tone}>{st.label}</Pill>
                    <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-5">
          <Title>История</Title>
          <ol className="space-y-2">
            {c.history.map((h, i) => (
              <li key={`${h.at}-${i}`} className="flex gap-3 text-sm">
                <span className="w-24 shrink-0 text-[11px] text-muted-foreground tabular-nums">{fmtDateTime(h.at)}</span>
                <span className="min-w-0 [overflow-wrap:anywhere]">
                  {h.source === 'created' ? 'Подписка создана' : h.source === 'audit' ? auditLabel(h.code) : ledgerLabel(h.code)}
                  {h.actor && <span className="text-muted-foreground"> · {h.actor}</span>}
                  {h.source === 'audit' && h.description && (
                    <span className="block text-xs text-muted-foreground">{h.description}</span>
                  )}
                </span>
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      <RotateSheet subId={c.id} open={rotateOpen} onClose={() => setRotateOpen(false)} />
      <ReasonSheet
        open={resetOpen}
        onClose={() => setResetOpen(false)}
        title="Сбросить все устройства"
        text="Все слоты освободятся, отключённые устройства вернутся. Приложения клиента зарегистрируются заново при следующем обновлении подписки."
        confirmLabel="Сбросить"
        mutation={(reason) => api.resetSubscriptionDevices(c.id, reason)}
        onDone={(r) => {
          toast.success(`Удалено устройств: ${r.devices_deleted}${r.devices_failed ? `, не удалось: ${r.devices_failed}` : ''}`);
          setResetOpen(false);
        }}
        subId={c.id}
      />
    </div>
  );
}

function Tile({ label, value, sub }: { label: string; value: string; sub?: string }) {
  return (
    <div className="rounded-2xl bg-card border border-white/5 p-3 min-w-0">
      <div className="text-[10px] font-bold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 text-base font-bold truncate">{value}</div>
      {sub && <div className="text-[11px] text-muted-foreground truncate">{sub}</div>}
    </div>
  );
}

function Hints({ c }: { c: OwnerSubscriptionCard }) {
  if (!c.hints.length) {
    return (
      <div className="flex items-start gap-2 rounded-2xl bg-green-500/10 px-4 py-3 text-sm">
        <CheckCircle2 className="mt-0.5 w-4 h-4 shrink-0 text-green-500" />
        <span>
          Со стороны подписки всё в порядке: активна, у поставщика сходится, слоты есть. Попросите
          клиента обновить подписку в приложении.
        </span>
      </div>
    );
  }
  return (
    <Card>
      <CardContent className="p-5">
        <Title>Что может мешать подключению</Title>
        <ul className="space-y-2.5">
          {c.hints.map((code) => {
            const h = subscriptionHint(code);
            return (
              <li key={code} className="space-y-0.5">
                <span className={`inline-block rounded-lg px-2 py-1 text-xs font-semibold ${TONE_CLS[h.tone]}`}>{h.text}</span>
                {h.todo && <div className="text-xs text-muted-foreground">{h.todo}</div>}
                {code === 'supplier_unreachable' && c.live.error && c.live.error !== 'not_found' && (
                  <div className="text-xs text-muted-foreground [overflow-wrap:anywhere]">({c.live.error})</div>
                )}
              </li>
            );
          })}
        </ul>
      </CardContent>
    </Card>
  );
}

function ReasonSheet<T>({
  open,
  onClose,
  title,
  text,
  confirmLabel,
  mutation,
  onDone,
  subId,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  text: string;
  confirmLabel: string;
  mutation: (reason: string) => Promise<T>;
  onDone: (r: T) => void;
  subId: string;
}) {
  const [reason, setReason] = useState('');
  const qc = useQueryClient();
  const mut = useMutation({
    mutationFn: () => mutation(reason.trim()),
    onSuccess: (r) => {
      haptic('success');
      setReason('');
      void qc.invalidateQueries({ queryKey: ['owner-sub-card', subId] });
      void qc.invalidateQueries({ queryKey: ['client'] });
      onDone(r);
    },
    onError: (e) => {
      haptic('error');
      toast.error(apiErrorMessage(e) ?? 'Не получилось, попробуйте ещё раз');
    },
  });
  return (
    <BottomSheet open={open} onClose={onClose} title={title}>
      <div className="space-y-4">
        <p className="text-sm text-muted-foreground">{text}</p>
        <input
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          maxLength={200}
          placeholder="Причина: сменил телефон"
          className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-base outline-none focus:border-primary/60"
        />
        <Button className="w-full" disabled={mut.isPending || reason.trim().length < 2} onClick={() => mut.mutate()}>
          {mut.isPending ? <Loader2 className="animate-spin" /> : confirmLabel}
        </Button>
      </div>
    </BottomSheet>
  );
}

function RotateSheet({ subId, open, onClose }: { subId: string; open: boolean; onClose: () => void }) {
  const [newUrl, setNewUrl] = useState<string | null>(null);
  const close = () => {
    setNewUrl(null);
    onClose();
  };
  if (newUrl) {
    return (
      <BottomSheet open={open} onClose={close} title="Новая ссылка">
        <div className="space-y-4">
          <p className="text-sm text-muted-foreground">
            Старая ссылка больше не работает. Отправьте клиенту новую: удалить подписку в приложении
            и добавить эту.
          </p>
          <div className="rounded-xl bg-white/5 px-3 py-2">
            <CopyValue value={newUrl} title="новая ссылка" />
          </div>
          <Button className="w-full" onClick={close}>
            Готово
          </Button>
        </div>
      </BottomSheet>
    );
  }
  return (
    <ReasonSheet
      open={open}
      onClose={close}
      title="Выдать новую ссылку"
      text="Старая ссылка перестанет отдавать подписку, отключённые устройства вернутся. Уже скачанные профили продолжат работать до смены серверов: ключи у AdaptGroup не меняются."
      confirmLabel="Выдать"
      mutation={(reason) => api.rotateSubscriptionLink(subId, reason)}
      onDone={(r) => setNewUrl(r.subscription_url)}
      subId={subId}
    />
  );
}
